import { useContext, useEffect, useRef } from 'react';
import { gql, useQuery } from '@apollo/client';
import { UserInfoContext } from '@/contexts/UserInfoContext';
import { maybeInitSentry } from '@/sentry';

const GET_USER_DETAILS = gql`
  query GetUserDetails {
    userDetails {
      defaultS3Endpoint
      defaultDevoEndpoint
      features {
        agentCollaboration
        automationsToolsEnabled
        caseManagementToolsEnabled
        constructIntegration
        customAgents
        devoAssistantEnabled
        devoToolsEnabled
        gitlabToolsEnabled
        knowledgebasesEdit
        knowledgebasesViewOnly
        mcpManagement
        mcpToolSelection
        secOpsToolsEnabled
        secretsManagementEnabled
        webhooksEnabled
        workflowBuilderEnabled
        externalConnectors
        workflowsManage
        sentryUnmaskData
        asocV2Enabled
        dataRoutingEnabled
      }
    }
  }
`;

interface UserDetailsResponse {
  userDetails: {
    defaultS3Endpoint?: string | null;
    defaultDevoEndpoint?: string | null;
    features: {
      agentCollaboration: boolean;
      automationsToolsEnabled: boolean;
      caseManagementToolsEnabled: boolean;
      constructIntegration: boolean;
      customAgents: boolean;
      devoAssistantEnabled: boolean;
      devoToolsEnabled: boolean;
      gitlabToolsEnabled: boolean;
      knowledgebasesEdit: boolean;
      knowledgebasesViewOnly: boolean;
      mcpManagement: boolean;
      mcpToolSelection: boolean;
      secOpsToolsEnabled: boolean;
      secretsManagementEnabled: boolean;
      webhooksEnabled: boolean;
      workflowBuilderEnabled: boolean;
      externalConnectors: boolean;
      workflowsManage: boolean;
      sentryUnmaskData: boolean;
      asocV2Enabled: boolean;
      dataRoutingEnabled: boolean;
    };
  };
}

/**
 * Hook to fetch and update user details with USER-SPECIFIC features.
 * This fetches features that are specific to the authenticated user.
 *
 * Note: Public features (like auth method flags) are handled separately
 * by usePublicFeatures hook and are not user-specific.
 */
export const useUserDetailsWithFeatures = () => {
  const { userInfo, setUserInfo } = useContext(UserInfoContext);
  const hasUpdatedFeatures = useRef(false);

  // Skip if no userInfo (not authenticated)
  // Auth is via session cookie, so we just check if userInfo exists
  const shouldSkip = !userInfo?.id;

  const { data, loading, error } = useQuery<UserDetailsResponse>(GET_USER_DETAILS, {
    skip: shouldSkip, // Only fetch if user is authenticated
    fetchPolicy: 'cache-and-network', // Always check server for fresh features
  });

  useEffect(() => {
    // Only update features once when data is first available
    if (data?.userDetails?.features && userInfo && !hasUpdatedFeatures.current) {
      // Merge fresh user-specific features with existing public features
      // Preserve public features (devo/keycloak auth) that may have been set during login
      const mergedFeatures = {
        ...userInfo.features, // Keep existing features (including public ones)
        ...data.userDetails.features, // Override with fresh user-specific features
        // Ensure all boolean fields are properly defined
        gitlabToolsEnabled:
          data.userDetails.features.gitlabToolsEnabled ??
          userInfo.features?.gitlabToolsEnabled ??
          false,
      };

      setUserInfo({
        ...userInfo,
        features: mergedFeatures,
        defaultS3Endpoint: data.userDetails.defaultS3Endpoint ?? userInfo.defaultS3Endpoint ?? null,
        defaultDevoEndpoint: data.userDetails.defaultDevoEndpoint ?? userInfo.defaultDevoEndpoint ?? null,
      });
      maybeInitSentry(mergedFeatures.sentryUnmaskData);

      hasUpdatedFeatures.current = true;
    }
  }, [data, userInfo, setUserInfo]);

  return { loading, error, features: data?.userDetails?.features };
};
